import { useEffect, useState, type FormEvent, type ReactNode } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { supabase } from '../../lib/supabase';
import { useAuth } from '../../hooks/useAuth';
import { PageHeader } from '../../components/ui/PageHeader';
import { DIAS_SEMANA } from '../../lib/horarios';
import type { EsquemaRemuneracionRow, EstadoGeneral, ServicioRow } from '../../types/database.types';
import { ROLES } from '../../lib/roles';

interface HorarioDia {
  activo: boolean;
  hora_inicio: string;
  hora_fin: string;
}

const HORARIO_VACIO: HorarioDia[] = DIAS_SEMANA.map(() => ({
  activo: false,
  hora_inicio: '09:00',
  hora_fin: '17:00',
}));

interface FormState {
  nombre: string;
  documento: string;
  telefono: string;
  whatsapp: string;
  email: string;
  tipo_vinculacion: string;
  fecha_ingreso: string;
  estado: EstadoGeneral;
  esquema_remuneracion_id: string;
  usuario_id: string;
}

const VACIO: FormState = {
  nombre: '',
  documento: '',
  telefono: '',
  whatsapp: '',
  email: '',
  tipo_vinculacion: '',
  fecha_ingreso: '',
  estado: 'activo',
  esquema_remuneracion_id: '',
  usuario_id: '',
};

interface UsuarioProfesionalOpcion {
  id: string;
  nombre: string;
  email: string | null;
}

/**
 * Alta y edición de profesionales (DAF v2, sección 8), incluyendo qué
 * servicios puede realizar cada una (tabla profesional_servicios) — es lo
 * que decide qué aparece en el selector de profesional del módulo de
 * Ventas y, más adelante, del portal de reservas online.
 *
 * También incluye el horario laboral semanal (tabla horarios_profesionales)
 * — es la base que usa el motor de disponibilidad (`calcularSlotsDisponibles`,
 * DAF v2 sección 9) tanto en la agenda interna como en el portal público de
 * reservas: sin esto configurado, una profesional no tiene ninguna hora
 * disponible para reservar online.
 *
 * Incluye también vincular la profesional a su cuenta de login propia
 * (`usuario_id`) — hallazgo real reportado por el usuario probando con una
 * cuenta de profesional: sin este campo (que originalmente quedó fuera de
 * este formulario, solo administrable a mano en Supabase) era muy fácil
 * crear el perfil de la profesional y crear su usuario de acceso por
 * separado sin enlazarlos nunca, y esa profesional entonces no veía NINGUNA
 * de sus propias citas al iniciar sesión — la política RLS
 * `citas_profesional_select` depende enteramente de `auth_profesional_id()`,
 * que busca la fila de `profesionales` cuyo `usuario_id` sea igual al uid
 * de quien inició sesión; sin ese enlace, la búsqueda no encuentra nada y
 * la profesional ve la agenda vacía, sin ningún error visible. El selector
 * de abajo solo ofrece usuarios con rol "profesional" que todavía no estén
 * enlazados a otra profesional (para no enlazar la misma cuenta dos veces
 * por accidente), y siempre incluye la opción "Sin vincular" para un perfil
 * que todavía no tiene login propio.
 *
 * Descansos dentro del día y bloqueos especiales quedan para un siguiente
 * incremento; por ahora se administran directo en Supabase si se necesitan
 * antes de que exista esa pantalla.
 */
export function ProfesionalForm() {
  const { id } = useParams<{ id: string }>();
  const esNueva = id === 'nuevo';
  const navigate = useNavigate();
  const { perfil } = useAuth();

  const [servicios, setServicios] = useState<ServicioRow[]>([]);
  const [esquemas, setEsquemas] = useState<EsquemaRemuneracionRow[]>([]);
  const [usuariosDisponibles, setUsuariosDisponibles] = useState<UsuarioProfesionalOpcion[]>([]);
  const [serviciosAutorizados, setServiciosAutorizados] = useState<Set<string>>(new Set());
  const [horario, setHorario] = useState<HorarioDia[]>(HORARIO_VACIO);
  const [form, setForm] = useState<FormState>(VACIO);
  const [loading, setLoading] = useState(!esNueva);
  const [guardando, setGuardando] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!perfil) return;

    supabase
      .from('servicios')
      .select('*')
      .eq('estado', 'activo')
      .order('nombre')
      .then(({ data }) => data && setServicios(data));

    supabase
      .from('esquemas_remuneracion')
      .select('*')
      .eq('estado', 'activo')
      .order('nombre')
      .then(({ data }) => data && setEsquemas(data));

    // Candidatos a "cuenta de acceso": usuarios con rol profesional de este
    // salón que todavía no estén enlazados a OTRA fila de `profesionales`
    // (la propia, si se está editando, se excluye de la exclusión más abajo
    // para que su selección actual siga apareciendo en la lista).
    Promise.all([
      supabase.from('roles').select('id').eq('codigo', ROLES.PROFESIONAL).single(),
      supabase.from('usuarios').select('id, nombre, email, rol_id').eq('salon_id', perfil.salon_id),
      supabase.from('profesionales').select('id, usuario_id').eq('salon_id', perfil.salon_id),
    ]).then(([rolRes, usuariosRes, profesionalesRes]) => {
      if (!rolRes.data || !usuariosRes.data) return;
      const enlazadosPorOtra = new Set(
        (profesionalesRes.data ?? [])
          .filter((p) => p.usuario_id && p.id !== id)
          .map((p) => p.usuario_id as string),
      );
      const candidatos = usuariosRes.data
        .filter((u) => u.rol_id === rolRes.data!.id && !enlazadosPorOtra.has(u.id))
        .map((u) => ({ id: u.id, nombre: u.nombre, email: u.email }));
      setUsuariosDisponibles(candidatos);
    });
  }, [perfil, id]);

  useEffect(() => {
    if (esNueva || !id) return;

    Promise.all([
      supabase.from('profesionales').select('*').eq('id', id).single(),
      supabase.from('profesional_servicios').select('servicio_id').eq('profesional_id', id),
      supabase.from('horarios_profesionales').select('*').eq('profesional_id', id),
    ]).then(([prof, ps, hp]) => {
      if (prof.error || !prof.data) {
        setError(prof.error?.message ?? 'No se encontró la profesional');
      } else {
        setForm({
          nombre: prof.data.nombre,
          documento: prof.data.documento ?? '',
          telefono: prof.data.telefono ?? '',
          whatsapp: prof.data.whatsapp ?? '',
          email: prof.data.email ?? '',
          tipo_vinculacion: prof.data.tipo_vinculacion ?? '',
          fecha_ingreso: prof.data.fecha_ingreso ?? '',
          estado: prof.data.estado,
          esquema_remuneracion_id: prof.data.esquema_remuneracion_id ?? '',
          usuario_id: prof.data.usuario_id ?? '',
        });
        setServiciosAutorizados(new Set((ps.data ?? []).map((r) => r.servicio_id)));

        if (hp.data && hp.data.length > 0) {
          const nuevo = HORARIO_VACIO.map((d) => ({ ...d }));
          for (const fila of hp.data) {
            nuevo[fila.dia_semana] = {
              activo: true,
              hora_inicio: fila.hora_inicio.slice(0, 5),
              hora_fin: fila.hora_fin.slice(0, 5),
            };
          }
          setHorario(nuevo);
        }
      }
      setLoading(false);
    });
  }, [esNueva, id]);

  function actualizar<K extends keyof FormState>(campo: K, valor: FormState[K]) {
    setForm((f) => ({ ...f, [campo]: valor }));
  }

  function alternarServicio(servicioId: string) {
    setServiciosAutorizados((prev) => {
      const next = new Set(prev);
      if (next.has(servicioId)) next.delete(servicioId);
      else next.add(servicioId);
      return next;
    });
  }

  function actualizarDia(indice: number, cambios: Partial<HorarioDia>) {
    setHorario((prev) => prev.map((d, i) => (i === indice ? { ...d, ...cambios } : d)));
  }

  async function sincronizarHorario(profesionalId: string) {
    // Mismo enfoque que sincronizarServiciosAutorizados: borrar y volver a
    // insertar es correcto y simple para, a lo sumo, 7 filas por profesional.
    await supabase.from('horarios_profesionales').delete().eq('profesional_id', profesionalId);

    const filas = horario
      .map((d, dia_semana) => ({ d, dia_semana }))
      .filter(({ d }) => d.activo)
      .map(({ d, dia_semana }) => ({
        profesional_id: profesionalId,
        dia_semana,
        hora_inicio: d.hora_inicio,
        hora_fin: d.hora_fin,
      }));

    if (filas.length > 0) {
      await supabase.from('horarios_profesionales').insert(filas);
    }
  }

  async function sincronizarServiciosAutorizados(profesionalId: string) {
    // Enfoque simple y correcto para el tamaño de este formulario: borrar
    // las asignaciones existentes y volver a insertar las seleccionadas,
    // en vez de calcular un diff — el volumen de filas es pequeño (un
    // profesional autoriza, como mucho, unas pocas decenas de servicios).
    await supabase.from('profesional_servicios').delete().eq('profesional_id', profesionalId);

    if (serviciosAutorizados.size > 0) {
      const filas = Array.from(serviciosAutorizados).map((servicio_id) => ({
        profesional_id: profesionalId,
        servicio_id,
      }));
      await supabase.from('profesional_servicios').insert(filas);
    }
  }

  async function handleSubmit(e: FormEvent) {
    e.preventDefault();
    if (!perfil) return;
    // useParams() tipa `id` como `string | undefined` aunque la ruta
    // siempre lo trae ('nuevo' o un uuid real) -- este guard se lo
    // confirma a TypeScript para el resto de la función, incluyendo
    // `let profesionalId = id;` y `.eq('id', id)` más abajo.
    if (!id) return;
    setGuardando(true);
    setError(null);

    const payload = {
      salon_id: perfil.salon_id,
      nombre: form.nombre,
      documento: form.documento || null,
      telefono: form.telefono || null,
      whatsapp: form.whatsapp || null,
      email: form.email || null,
      tipo_vinculacion: form.tipo_vinculacion || null,
      fecha_ingreso: form.fecha_ingreso || null,
      estado: form.estado,
      esquema_remuneracion_id: form.esquema_remuneracion_id || null,
      usuario_id: form.usuario_id || null,
    };

    let profesionalId = id;

    if (esNueva) {
      const { data, error: err } = await supabase.from('profesionales').insert(payload).select('id').single();
      if (err || !data) {
        setError(err?.message ?? 'No se pudo crear la profesional');
        setGuardando(false);
        return;
      }
      profesionalId = data.id;
    } else {
      const { error: err } = await supabase.from('profesionales').update(payload).eq('id', id);
      if (err) {
        setError(err.message);
        setGuardando(false);
        return;
      }
    }

    if (profesionalId) {
      await sincronizarServiciosAutorizados(profesionalId);
      await sincronizarHorario(profesionalId);
    }

    setGuardando(false);
    navigate('/admin/profesionales');
  }

  if (loading) return <p className="text-sm text-onyx/50">Cargando…</p>;

  return (
    <div className="max-w-2xl">
      <PageHeader title={esNueva ? 'Nueva profesional' : 'Editar profesional'} />

      <form onSubmit={handleSubmit} className="card space-y-4">
        <div className="grid grid-cols-2 gap-4">
          <Campo label="Nombre" required>
            <input
              required
              className="input-field"
              value={form.nombre}
              onChange={(e) => actualizar('nombre', e.target.value)}
            />
          </Campo>
          <Campo label="Documento">
            <input
              className="input-field"
              value={form.documento}
              onChange={(e) => actualizar('documento', e.target.value)}
            />
          </Campo>
        </div>

        <div className="grid grid-cols-2 gap-4">
          <Campo label="Teléfono">
            <input
              className="input-field"
              value={form.telefono}
              onChange={(e) => actualizar('telefono', e.target.value)}
            />
          </Campo>
          <Campo label="WhatsApp">
            <input
              className="input-field"
              value={form.whatsapp}
              onChange={(e) => actualizar('whatsapp', e.target.value)}
            />
          </Campo>
        </div>

        <Campo label="Correo electrónico">
          <input
            type="email"
            className="input-field"
            value={form.email}
            onChange={(e) => actualizar('email', e.target.value)}
          />
        </Campo>

        <div className="grid grid-cols-3 gap-4">
          <Campo label="Tipo de vinculación">
            <input
              className="input-field"
              placeholder="Empleada, independiente…"
              value={form.tipo_vinculacion}
              onChange={(e) => actualizar('tipo_vinculacion', e.target.value)}
            />
          </Campo>
          <Campo label="Fecha de ingreso">
            <input
              type="date"
              className="input-field"
              value={form.fecha_ingreso}
              onChange={(e) => actualizar('fecha_ingreso', e.target.value)}
            />
          </Campo>
          <Campo label="Estado">
            <select
              className="input-field"
              value={form.estado}
              onChange={(e) => actualizar('estado', e.target.value as EstadoGeneral)}
            >
              <option value="activo">Activo</option>
              <option value="inactivo">Inactivo</option>
            </select>
          </Campo>
        </div>

        <Campo label="Esquema de remuneración">
          <div className="flex gap-2">
            <select
              className="input-field"
              value={form.esquema_remuneracion_id}
              onChange={(e) => actualizar('esquema_remuneracion_id', e.target.value)}
            >
              <option value="">Sin asignar</option>
              {esquemas.map((esq) => (
                <option key={esq.id} value={esq.id}>
                  {esq.nombre}
                </option>
              ))}
            </select>
            <Link to="/admin/comisiones/esquemas" className="btn-secondary whitespace-nowrap">
              Gestionar
            </Link>
          </div>
          <p className="mt-1 text-xs text-onyx/40">
            Determina cómo se calculan sus comisiones — ver módulo de Comisiones.
          </p>
        </Campo>

        <Campo label="Cuenta de acceso (usuario de login)">
          <select
            className="input-field"
            value={form.usuario_id}
            onChange={(e) => actualizar('usuario_id', e.target.value)}
          >
            <option value="">Sin vincular</option>
            {usuariosDisponibles.map((u) => (
              <option key={u.id} value={u.id}>
                {u.nombre} {u.email ? `(${u.email})` : ''}
              </option>
            ))}
          </select>
          <p className="mt-1 text-xs text-onyx/40">
            Vincula esta profesional con su usuario de login (creado en <Link to="/admin/usuarios" className="underline">Usuarios</Link>,
            con rol "Profesional"). Sin este enlace, esa persona puede iniciar sesión pero su Agenda le va a aparecer
            siempre vacía — no va a ver ninguna de sus propias citas.
          </p>
          {usuariosDisponibles.length === 0 && (
            <p className="mt-1 text-xs text-amber-700">
              No hay ningún usuario con rol "Profesional" disponible para vincular todavía — créalo primero en
              Usuarios.
            </p>
          )}
        </Campo>

        <div>
          <span className="mb-2 block text-sm font-medium text-onyx/80">Servicios que puede realizar</span>
          {servicios.length === 0 && <p className="text-sm text-onyx/50">Todavía no hay servicios creados.</p>}
          <div className="grid grid-cols-2 gap-2 rounded-md border border-onyx/10 p-3">
            {servicios.map((s) => (
              <label key={s.id} className="flex items-center gap-2 text-sm text-onyx/80">
                <input
                  type="checkbox"
                  checked={serviciosAutorizados.has(s.id)}
                  onChange={() => alternarServicio(s.id)}
                />
                {s.nombre}
              </label>
            ))}
          </div>
        </div>

        <div>
          <span className="mb-2 block text-sm font-medium text-onyx/80">Horario laboral</span>
          <p className="mb-2 text-xs text-onyx/50">
            Define en qué días y horas atiende esta profesional. Es lo que el portal público de reservas usa para
            calcular disponibilidad — un día sin marcar no ofrecerá ninguna hora para reservar.
          </p>
          <div className="space-y-2 rounded-md border border-onyx/10 p-3">
            {DIAS_SEMANA.map((nombreDia, i) => (
              <div key={nombreDia} className="flex items-center gap-3 text-sm">
                <label className="flex w-32 items-center gap-2 text-onyx/80">
                  <input
                    type="checkbox"
                    checked={horario[i].activo}
                    onChange={(e) => actualizarDia(i, { activo: e.target.checked })}
                  />
                  {nombreDia}
                </label>
                <input
                  type="time"
                  className="input-field"
                  disabled={!horario[i].activo}
                  value={horario[i].hora_inicio}
                  onChange={(e) => actualizarDia(i, { hora_inicio: e.target.value })}
                />
                <span className="text-onyx/40">a</span>
                <input
                  type="time"
                  className="input-field"
                  disabled={!horario[i].activo}
                  value={horario[i].hora_fin}
                  onChange={(e) => actualizarDia(i, { hora_fin: e.target.value })}
                />
              </div>
            ))}
          </div>
        </div>

        {error && <p className="text-sm text-red-600">{error}</p>}

        <div className="flex gap-3 pt-2">
          <button type="submit" disabled={guardando} className="btn-primary">
            {guardando ? 'Guardando…' : 'Guardar'}
          </button>
          <button type="button" className="btn-secondary" onClick={() => navigate('/admin/profesionales')}>
            Cancelar
          </button>
        </div>
      </form>
    </div>
  );
}

function Campo({ label, required, children }: { label: string; required?: boolean; children: ReactNode }) {
  return (
    <label className="block">
      <span className="mb-1 block text-sm font-medium text-onyx/80">
        {label}
        {required && <span className="text-gold-deep"> *</span>}
      </span>
      {children}
    </label>
  );
}
